import React from 'react';
import Form from 'react-bootstrap/Form';
import Stack from 'react-bootstrap/Stack';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import CareMapprIconButton from '../../../Button/CareMapprIconButton';
import { usePatientProfile } from '../../hooks';
import { RootState } from '../../../../../store';
import { mapToEpisodeDetailsKey } from '../AdditionalSection/utils';
import { setPatientProfileState } from '../../../../../store/ai2';
import DataMapperProfileBar from '../../../DataMapper/DataMapperProfileBar';
import EpisodeDetailActionButtons from '../AdditionalSection/EpisodeDetailActionButtons';
import { Edit } from '../../../Icons';
import type { SectionField } from '../AdditionalSection/types';

function toTime(value: unknown): number {
  if (!value) return 0;
  const t = Date.parse(String(value));
  return Number.isFinite(t) ? t : 0;
}

function getLatestEpisodeIndex(episodes: any[]): number {
  if (!Array.isArray(episodes) || episodes.length === 0) return 0;
  const openEpisodes = episodes.filter(
    (ep) =>
      ep?.episode_closed === false && (ep?.episode_closed_date ?? null) == null,
  );
  const candidates = openEpisodes.length > 0 ? openEpisodes : episodes;
  let bestIdxInCandidates = 0;
  let bestOpened = -1;
  let bestUpdated = -1;
  let bestIdScore = -1;
  for (let i = 0; i < candidates.length; i++) {
    const ep = candidates[i];
    const opened = toTime(ep?.episode_opened_date);
    const updated = toTime(ep?.updated_at);
    const idScore =
      typeof ep?.id === 'number'
        ? ep.id
        : typeof ep?.id === 'string'
          ? Number(ep.id)
          : 0;
    if (opened > bestOpened) {
      bestOpened = opened;
      bestUpdated = updated;
      bestIdScore = idScore;
      bestIdxInCandidates = i;
      continue;
    }
    if (opened < bestOpened) continue;

    if (updated > bestUpdated) {
      bestUpdated = updated;
      bestIdScore = idScore;
      bestIdxInCandidates = i;
      continue;
    }
    if (updated < bestUpdated) continue;

    if (idScore > bestIdScore) {
      bestIdScore = idScore;
      bestIdxInCandidates = i;
    }
  }
  const bestEpisode = candidates[bestIdxInCandidates];
  const idx = episodes.indexOf(bestEpisode);
  return idx >= 0 ? idx : 0;
}

function normalizeEpisodeDetails(details: any): Record<string, any> {
  if (Array.isArray(details)) {
    return details.reduce<Record<string, any>>((acc, cur) => {
      const key =
        typeof cur?.meta_name === 'string' ? cur.meta_name.trim() : '';
      if (!key) return acc;
      const v =
        typeof cur?.meta_value === 'string' ? cur.meta_value : cur?.meta_value;
      acc[key] = v;
      return acc;
    }, {});
  }
  if (details && typeof details === 'object') {
    return { ...details };
  }
  return {};
}

function upsertEpisodeDetails(
  details: any,
  key: string,
  nextValue: string | null,
) {
  if (Array.isArray(details)) {
    const next = details.filter(
      (d: any) =>
        String(d?.meta_name ?? '').trim() &&
        String(d?.meta_name ?? '').trim() !== key,
    );
    if (nextValue === null) return next;
    return [...next, { meta_name: key, meta_value: nextValue }];
  }
  if (details && typeof details === 'object') {
    const next = { ...details };
    if (nextValue === null) {
      delete (next as any)[key];
    } else {
      (next as any)[key] = nextValue;
    }
    return next;
  }
  if (nextValue === null) return details;
  return [{ meta_name: key, meta_value: nextValue }];
}

export const EpisodeDetailOptionsField = ({
  title,
  editable,
  clinicId,
  options,
}: {
  title: string;
  editable: boolean;
  clinicId?: number;
  options: NonNullable<SectionField['options']>;
}) => {
  const { form, onSubmit, patientProfile } = usePatientProfile();
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const patientAccessToken = searchParams.get('access_token');
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const clinic = React.useMemo(() => {
    const clinics = Array.isArray(patientProfile?.clinics)
      ? patientProfile.clinics
      : [];
    if (typeof clinicId === 'number') {
      return clinics.find((c: any) => c?.id === clinicId) ?? clinics[0];
    }
    return clinics[0];
  }, [patientProfile?.clinics, clinicId]);
  const episodes = Array.isArray(clinic?.episodes) ? clinic.episodes : [];
  const latestEpisodeIndex = React.useMemo(
    () => getLatestEpisodeIndex(episodes),
    [episodes],
  );
  const latestEpisode = episodes[latestEpisodeIndex];

  const detailsKey = mapToEpisodeDetailsKey(title) as unknown as string;
  const detailsObj = normalizeEpisodeDetails(latestEpisode?.details);
  const rawValue = detailsObj?.[detailsKey] as any;

  const normalizedOptions = React.useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.filter(
      (opt) =>
        opt &&
        typeof opt.label === 'string' &&
        typeof opt.value === 'string' &&
        opt.label.trim(),
    );
  }, [options]);

  const unknownOption = React.useMemo(() => {
    const isUnknown = (opt: { label: string; value: string }) => {
      const label = opt.label.trim().toLowerCase();
      const value = opt.value.trim().toLowerCase();
      return label === 'unknown' || value === 'unknown';
    };
    return normalizedOptions.find(isUnknown);
  }, [normalizedOptions]);

  const UNKNOWN_SENTINEL = '__cm_unknown__';

  const selectedValue = React.useMemo(() => {
    if (rawValue == null) return null;
    const raw = String(rawValue).trim();
    if (!raw) return null;

    const rawKey = raw.toLowerCase();
    const matched = normalizedOptions.find(
      (opt) => opt.value.trim().toLowerCase() === rawKey,
    );
    if (matched) {
      if (
        unknownOption &&
        matched.value.trim().toLowerCase() ===
          unknownOption.value.trim().toLowerCase()
      ) {
        return null;
      }
      return matched.value;
    }
    return null;
  }, [rawValue, normalizedOptions, unknownOption]);

  const [isEditing, setIsEditing] = React.useState(false);
  const [draftValue, setDraftValue] = React.useState<string | null>(
    selectedValue,
  );

  React.useEffect(() => {
    if (isEditing) return;
    setDraftValue(selectedValue);
  }, [selectedValue, isEditing]);

  const label = React.useMemo(() => {
    if (selectedValue == null) return unknownOption?.label ?? '-';
    const matched = normalizedOptions.find(
      (opt) =>
        opt.value.trim().toLowerCase() === String(selectedValue).toLowerCase(),
    );
    return matched?.label ?? String(selectedValue);
  }, [selectedValue, normalizedOptions, unknownOption]);

  const onConfirm = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!selectedTenancy || !patientAccessToken) {
      setIsEditing(false);
      return;
    }

    const updatedEpisode = {
      ...(latestEpisode ?? {}),
      details: upsertEpisodeDetails(
        latestEpisode?.details,
        detailsKey,
        draftValue == null ? null : draftValue,
      ),
    };

    const updatedEpisodes = episodes.map((ep: any, idx: number) =>
      idx === latestEpisodeIndex ? updatedEpisode : ep,
    );

    const updatedClinics = Array.isArray(patientProfile?.clinics)
      ? patientProfile.clinics.map((cl: any, idx: number) => {
          if (typeof clinicId === 'number') {
            if (cl?.id !== clinicId) return cl;
            return { ...cl, episodes: updatedEpisodes };
          }
          if (idx !== 0) return cl;
          return { ...cl, episodes: updatedEpisodes };
        })
      : [];

    const updatedPatientProfile = {
      ...patientProfile,
      clinics: updatedClinics,
    };

    dispatch(setPatientProfileState({ patientProfile: updatedPatientProfile }));

    const formData = DataMapperProfileBar(
      form.formState,
      updatedPatientProfile,
      patientAccessToken,
      selectedTenancy,
      [],
    );
    onSubmit(formData);
    setIsEditing(false);
  };

  const onCancel = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDraftValue(selectedValue);
    setIsEditing(false);
  };

  return (
    <Stack direction="horizontal" gap={2} className="labelRow w-100">
      <Form.Label className="labelBold">{title}</Form.Label>
      {!isEditing ? (
        <>
          <Form.Label className="labelSimple">{label}</Form.Label>
          {editable ? (
            <CareMapprIconButton
              className="m-0 p-0 min-width-auto"
              icon={<Edit size={14} />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsEditing(true);
              }}
            />
          ) : null}
        </>
      ) : (
        <>
          <Form.Select
            className="form-select-xs mb-1"
            name={title}
            id={title}
            value={draftValue == null ? UNKNOWN_SENTINEL : draftValue}
            onChange={(e) => {
              const v = e.target.value;
              if (v === UNKNOWN_SENTINEL) {
                setDraftValue(null);
                return;
              }
              setDraftValue(v);
            }}
          >
            {normalizedOptions.map((opt) => {
              const isUnknown = unknownOption ? opt === unknownOption : false;
              return (
                <option
                  key={`${opt.value}|${opt.label}`}
                  value={isUnknown ? UNKNOWN_SENTINEL : opt.value}
                >
                  {opt.label}
                </option>
              );
            })}
          </Form.Select>
          <EpisodeDetailActionButtons
            buttonClassName="m-0 p-0"
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        </>
      )}
    </Stack>
  );
};
